import { useEffect, useState } from 'react';
import { CheckCircle2, Circle, FlaskConical, Loader2, Radio } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';
import { formatApiError } from '../../lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const Check = ({ c }) => (
  <li className="flex items-start gap-2 text-sm" data-testid={`mode-check-${c.key}`}>
    {c.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> : <Circle className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />}
    <span className={c.ok ? 'text-slate-300' : 'text-slate-400'}>
      {c.label}{!c.required && <span className="text-slate-500"> (recommended)</span>}
    </span>
  </li>
);

const GoLiveDialog = ({ open, onOpenChange, demoRows, onConfirm, busy }) => {
  const [text, setText] = useState('');
  return (
    <AlertDialog open={open} onOpenChange={(o) => { setText(''); onOpenChange(o); }}>
      <AlertDialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="go-live-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Switch to Live?</AlertDialogTitle>
          <AlertDialogDescription className="text-slate-400">
            {demoRows} demo rows will be permanently removed, demo shortcuts turn off, and sales can post to production QuickBooks.
            Type <span className="font-mono text-white">GO LIVE</span> to confirm.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="GO LIVE"
          className="bg-slate-950 border-slate-800 text-white font-mono" data-testid="go-live-confirm-input" />
        <AlertDialogFooter>
          <AlertDialogCancel className="border-slate-700 bg-transparent text-slate-300" data-testid="go-live-cancel-btn">Cancel</AlertDialogCancel>
          <Button disabled={text.trim().toUpperCase() !== 'GO LIVE' || busy} onClick={() => onConfirm(text)}
            className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="go-live-confirm-btn">
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Go Live
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const AccountModeCard = () => {
  const { token, refreshMe, isImpersonating } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = () => api.getAccountMode(token).then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (fn, msg) => {
    setBusy(true);
    try {
      const r = await fn();
      setStatus(r.data);
      await refreshMe();
      toast.success(msg(r.data));
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;
  const live = status.mode === 'live';
  return (
    <Card id="environment" className={live ? 'bg-emerald-950/20 border-emerald-900/50' : 'bg-amber-950/20 border-amber-900/50'} data-testid="account-mode-card">
      <CardHeader>
        <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
          {live ? <Radio className="w-5 h-5 text-emerald-400" /> : <FlaskConical className="w-5 h-5 text-amber-300" />}
          Environment: <span data-testid="account-mode-value">{live ? 'Live' : 'Sandbox'}</span>
        </CardTitle>
        <CardDescription className="text-slate-400">
          {live
            ? 'Real POS data syncs to your books. Demo data and the shared demo POS are switched off.'
            : 'Explore with demo data and the shared demo POS. Sales never post to a production QuickBooks company in Sandbox.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!live && <ul className="space-y-2">{status.checks.map((c) => <Check key={c.key} c={c} />)}</ul>}
        <div className="flex flex-wrap gap-3">
          {live ? (
            <Button variant="outline" disabled={busy || isImpersonating} className="border-slate-700 text-slate-300"
              onClick={() => run(() => api.backToSandbox(token), () => 'Back in Sandbox mode')} data-testid="back-to-sandbox-btn">
              <FlaskConical className="w-4 h-4 mr-2" />Switch back to Sandbox
            </Button>
          ) : (
            <Button disabled={!status.ready || busy || isImpersonating} onClick={() => setOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="go-live-btn">
              <Radio className="w-4 h-4 mr-2" />Go Live
            </Button>
          )}
        </div>
        {isImpersonating && <p className="text-xs text-slate-500">Only the account owner can change the environment.</p>}
      </CardContent>
      <GoLiveDialog open={open} onOpenChange={setOpen} demoRows={status.demo_rows} busy={busy}
        onConfirm={(confirm) => run(() => api.goLive(token, confirm), (d) => `You're live — ${d.demo_rows_removed} demo rows removed. A summary is on its way to your inbox.`)} />
    </Card>
  );
};
